import { makeStyles } from '@mui/styles';
import { GridColDef } from '@mui/x-data-grid';
import React from 'react';

import DataGrid from '#app/Component/DataGrid';
import IconAction from '#app/Component/IconAction';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { transformObjectToDataGridRowsArray } from '#app/Util/DataGridDataTransformer';
import { momentDateFormatter } from '#app/Util/MomentDateFormatter';
import { Filters } from '#app/View/ClientService/OrderClientLitigeList';
import { useCollectionPaginated } from '#app/hooks/useCollectionPaginated';
import { ChannelTranslations } from '#app/typings/channel';
import { OrderClientStateTranslation } from '#app/typings/orderClient';
import { DisputeList } from '#app/typings/orderClientLitige';
import {
  DisputeType,
  DisputeTypeTranslations,
  DisputeState,
  DisputeStateTranslations,
} from '#app/typings/orderClientLitige';

type Props = Omit<ReturnType<typeof useCollectionPaginated<DisputeList, Filters>>, 'filters' | 'setFilters'>;

const List: React.FC<Props> = ({
  data: orderClientLitiges,
  totalItems,
  loading,
  pagination,
  sorting,
  setPagination,
  setSorting,
}) => {
  const classes = useStyles();

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: 'Référence',
      valueGetter: (_, row: DisputeList) => row.orderClient?.number,
      minWidth: 140,
    },
    {
      field: 'clientEmail',
      headerName: 'Email',
      valueGetter: (_, row: DisputeList) => row.orderClient?.client?.email,
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'clientName',
      headerName: 'Client',
      valueGetter: (_, row: DisputeList) => row.orderClient?.client?.name,
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'channel',
      headerName: 'Canal',
      valueGetter: (_, row: DisputeList) => ChannelTranslations[row.orderClient?.channel],
    },
    {
      field: 'carrier',
      headerName: 'Transporteur',
      valueGetter: (_, row: DisputeList) => row.orderClient?.orderClientCarrier?.label,
      minWidth: 120,
    },
    {
      field: 'orderClientState',
      headerName: 'Etat de la commande',
      valueGetter: (_, row: DisputeList) => OrderClientStateTranslation[row.orderClient?.orderClientState],
    },
    {
      field: 'type',
      headerName: 'Type de litige',
      valueGetter: (value: DisputeType) => DisputeTypeTranslations[value],
      minWidth: 135,
    },
    {
      field: 'state',
      headerName: 'Etat du litige',
      valueGetter: (value: DisputeState) => DisputeStateTranslations[value],
      minWidth: 130,
    },
    {
      field: 'author',
      headerName: 'Auteur du litige',
      valueGetter: (_, row: DisputeList) => row.authorLitige?.name,
      minWidth: 120,
    },
    {
      field: 'orderCreatedAt',
      headerName: 'Date de la commande',
      valueGetter: (_, row: DisputeList) => momentDateFormatter.dateTimeToFormatFr(row.orderClient?.createdAt),
      minWidth: 120,
    },
    {
      field: 'createdAt',
      headerName: 'Date de litige',
      valueGetter: (value: string) => momentDateFormatter.dateTimeToFormatFr(value),
      minWidth: 120,
    },
    {
      field: 'action',
      headerName: 'Action',
      type: 'actions',
      width: 70,
      getActions: ({ row }) => [
        <IconAction
          title="Visualiser"
          icon="view"
          to={getRoutePathname('client_service_order_client_litige', { uuid: row.uuid })}
        />,
      ],
    },
  ];

  return (
    <DataGrid
      className={classes.container}
      rows={transformObjectToDataGridRowsArray<DisputeList>(orderClientLitiges)}
      columns={columns}
      loading={loading}
      rowCount={totalItems}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
    />
  );
};

const useStyles = makeStyles({
  container: {
    marginTop: 15,
  },
});

export default List;
